import app from 'flarum/forum/app';
import FormModal from 'flarum/common/components/FormModal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import { describeAnswer, lookupBody, reasonProblem } from './whoIsThis';
/**
 * Ask who is behind one handle, with a reason -- Task 6.7, the frontend for
 * `POST /api/handle-lookups` (6.6).
 *
 * Says plainly, before anything is sent, that the lookup is recorded with the
 * asker's name, the handle and the reason. That is not decoration: §4.7's log
 * only deters if the person looking knows it exists.
 *
 * THE ANSWER LIVES IN THIS DIALOG AND NOWHERE ELSE. It is not put in the
 * store, not attached to the post, not remembered after closing. The next
 * look needs the next lookup, and the next log row.
 *
 * Errors arrive the standard way and are shown inside the dialog: a 422
 * carries the server's own wording about the 20-character rule and points at
 * the `reason` field (FormModal::onerror), and a 403 says permission was
 * denied. A custom errorHandler keeps them from ALSO appearing as a global
 * alert behind the dialog.
 */
export default class HandleLookupModal extends FormModal {
  answer = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.reason = Stream('');
  }
  className() {
    return 'IdeaSynqLookupModal Modal--small';
  }
  title() {
    return app.translator.trans('ideasynq-handles.forum.lookup.title', {
      handle: this.attrs.handle
    });
  }
  minLength() {
    const min = app.forum.attribute('handleLookupMinReason');
    return typeof min === 'number' && min > 0 ? min : 20;
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, this.answer ? this.answerView(this.answer) : this.formView());
  }
  formView() {
    const problem = reasonProblem(this.reason(), this.minLength());
    return m("div", {
      className: "Form"
    }, m("p", {
      className: "IdeaSynqLookupModal-warning"
    }, app.translator.trans('ideasynq-handles.forum.lookup.explanation')), m("div", {
      className: "Form-group"
    }, m("label", {
      for: "ideasynq-lookup-reason"
    }, app.translator.trans('ideasynq-handles.forum.lookup.reason_label')), m("textarea", {
      id: "ideasynq-lookup-reason",
      className: "FormControl",
      name: "reason",
      rows: 3,
      bidi: this.reason,
      disabled: this.loading
    }), m("p", {
      className: "helpText"
    }, app.translator.trans('ideasynq-handles.forum.lookup.reason_hint', {
      count: this.minLength()
    }))), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading,
      disabled: problem !== null
    }, app.translator.trans('ideasynq-handles.forum.lookup.submit'))));
  }
  answerView(answer) {
    const handle = m("strong", null, this.attrs.handle);
    return m("div", {
      className: "Form"
    }, m("p", {
      className: "IdeaSynqLookupModal-answer"
    }, answer.kind === 'person' ? app.translator.trans('ideasynq-handles.forum.lookup.result_person', {
      handle,
      username: m("strong", null, answer.username)
    }) : app.translator.trans('ideasynq-handles.forum.lookup.result_deleted', {
      handle
    })), m("p", {
      className: "helpText"
    }, app.translator.trans('ideasynq-handles.forum.lookup.result_logged')), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--block",
      onclick: () => this.hide()
    }, app.translator.trans('ideasynq-handles.forum.lookup.close'))));
  }
  onsubmit(e) {
    e.preventDefault();
    if (this.answer || reasonProblem(this.reason(), this.minLength()) !== null) return;
    this.loading = true;
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/handle-lookups',
      body: lookupBody(this.attrs.handle, this.reason(), this.attrs.postId),
      errorHandler: error => this.onerror(error)
    }).then(response => {
      this.answer = describeAnswer(response?.data?.attributes);
    }).catch(() => {
      // Already shown inside the dialog by onerror.
    }).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }
}